import { Box, Button, Grid } from "@mui/material";
import CustomInput from "../components/ui/CustomInput";
import PageHeader from "../components/ui/PageHeader";
import PageForm from "../components/ui/PageForm";
import CustomGridHeaderCell from "../components/ui/CustomGridHeaderCell";
import { useRef, useState, useEffect } from "react";
import CustomTable from "../components/ui/CustomTable";
import axios from "../api/axiosClient";
import CustomTablePagingInfo from "../components/ui/CustomTablePagingInfo";
import { useIntl } from "react-intl";
import { useLoading } from "../components/LoadingContext";
import { useSelector } from "react-redux";
import Chip from "@mui/material/Chip";
import moment from "moment";
import { handleError, errorToast, useKeyDown } from "../services/serviceBase";
import { getCompanies } from "../services/apiService";
import toast from "react-hot-toast";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const UploadUDMList = () => {
  const intl = useIntl();
  const { setLoading } = useLoading();
  const idCompany = useSelector((state) => state.common.company);

  const fileRef = useRef(null);
  const page = useRef(0);
  const maxPage = useRef(0);
  const rowsForPage = 10;

  const [shouldFilterCollapse, setShouldFilterCollapse] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [tableHeigth, setTableHeigth] = useState(260);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companiesItems, setCompaniesItems] = useState([]);

  useKeyDown(() => {
    handleSearch();
  }, ["Enter"]);

  const resizeTable = () => {
    var fromHeight = document.querySelector("#form").clientHeight;
    var headerHeight = document.querySelector("#header").clientHeight;
    var pagingInfoHeight = document.querySelector("#pagingInfo").clientHeight;
    var pageHeight = window.innerHeight;
    var tableHeight =
      pageHeight - fromHeight - headerHeight - pagingInfoHeight - 90;
    setTableHeigth(tableHeight);
  };

  useEffect(() => {
    window.addEventListener("resize", resizeTable);
    return () => {
      window.removeEventListener("resize", resizeTable);
    };
  }, []);

  useEffect(() => {
    getCompanies(
      (response) => {
        var items = [];
        if (response !== undefined) {
          response.map((x) => {
            items.push({ value: x.idCompany, text: x.companyDescription });
          });
        }
        setCompaniesItems(items);
      },
      (error) => {
        handleError(error, intl);
      },
    );
  }, []);

  useEffect(() => {
    getColumns();
  }, [rows, page, tableHeigth]);

  const getColumns = () => {
    setColumns([
      {
        field: "companyDescription",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "General.Brand" })}
          />
        ),
        flex: 1,
      },
      {
        field: "fileName",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "UploadUDM.FilePath" })}
          />
        ),
        flex: 1,
      },
      {
        field: "creationDate",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "UploadUDM.UploadDate" })}
          />
        ),
        renderCell: (params) => {
          return moment(params.row.creationDate).format("DD/MM/YYYY HH:mm:ss");
        },
        flex: 1,
      },
      {
        field: "status",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "UploadUDM.Status" })}
          />
        ),
        renderCell: (params) => {
          const colorMap = {
            Uploaded: "primary",
            Error: "error",
            Success: "success",
            InProgress: "warning",
          };
          const color = colorMap[params.row.status] ?? "default";
          return <Chip label={params.row.status} color={color} size="small" />;
        },
        flex: 1,
      },
      {
        field: "actionDownload",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "General.Download" })}
          />
        ),
        renderCell: (params) => {
          if (params.row.status !== "Success") {
            return null;
          }
          return (
            <IconButton
              size="large"
              color="primary"
              onClick={(event) => handleDownloadFile(event, params.row.idUploadUDMListInfo)}
            >
              <FileDownloadIcon />
            </IconButton>
          );
        },
        flex: 1,
      },
    ]);
  };

  const handleSearch = () => {
    setShouldFilterCollapse(false);
    setLoading(true);
    const params = new URLSearchParams([]);
    selectedCompanies.forEach((id) => params.append("idCompanyList", id));
    axios
      .get("/udm/search", { params })
      .then((response) => {
        setShouldFilterCollapse(true);
        setLoading(false);
        resizeTable();
        setRows(response.data);
        setTotalCount(response.data.length);
        maxPage.current = Math.ceil(response.data.length / rowsForPage);
      })
      .catch((error) => {
        setLoading(false);
        handleError(error, () => {
          errorToast(error, intl);
        });
      });
  };

  const handleUpload = () => {
    if (
      !fileRef.current ||
      !fileRef.current.files ||
      !fileRef.current.files[0]
    ) {
      toast.error(intl.formatMessage({ id: "General.SelectExcelFile" }));
      return;
    }
    setLoading(true);
    const data = new FormData();
    data.append("File", fileRef.current.files[0]);
    selectedCompanies.forEach((id) => data.append("IdCompanyList", id));

    axios
      .post("/udm/upload", data)
      .then((response) => {
        toast.success("Upload completed");
        handleSearch();
        setLoading(false);
        fileRef.current.value = null;
      })
      .catch((error) => {
        setLoading(false);
        handleError(error, intl);
      });
  };

  const handleDownload = () => {
    setLoading(true);
    const params = new URLSearchParams([]);
    if (selectedCompanies.length > 0) {
      selectedCompanies.forEach((id) => params.append("idCompanyList", id));
    }

    axios
      .get("/udm/download", { params })
      .then((response) => {
        setLoading(false);
        if (response.data) {
          window.open(response.data, "_blank");
        }
      })
      .catch((error) => {
        setLoading(false);
        handleError(error, intl);
      });
  };

  const handleNextPage = () => {
    if (page.current < maxPage.current) {
      page.current = page.current + 1;
      handleSearch();
    }
  };

  const handlePreviousPage = () => {
    page.current = page.current - 1;
    handleSearch();
  };

  const handletrasitionEnd = () => {
    resizeTable();
  };

  const handleDownloadFile = async (e, idUploadUDMListInfo) => {
    try {
      const response = await axios.post(`/udm/${idUploadUDMListInfo}/csv`, {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const contentDisposition = response.headers["content-disposition"];
      let fileName = "download.csv";

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^";\s]+)"?/)?.[1];
        if (fileNameMatch) {
          fileName = fileNameMatch;
        }
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il download:", error);
    }
  };

  return (
    <Box sx={{ overflow: "hidden" }}>
      <PageHeader title={intl.formatMessage({ id: "UploadUDMList.Title" })} />
      <PageForm
        adjustHeightFunc={handletrasitionEnd}
        shouldCollapse={shouldFilterCollapse}
        form={
          <form>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <CustomInput
                  labelText={intl.formatMessage({ id: "General.Brand" })}
                  type="multiselect"
                  valueRef={selectedCompanies}
                  selectItems={companiesItems}
                  setValue={setSelectedCompanies}
                  textCol={2}
                  inputCol={10}
                />
              </Grid>
              <Grid item md={5.5}>
                <CustomInput
                  labelText={intl.formatMessage({ id: "General.File" })}
                  type="file"
                  valueRef={fileRef}
                  sx={{ width: "100%", color: "red !important" }}
                  textCol={2}
                  inputCol={10}
                />
              </Grid>
              <Grid item md={0.5}>
                <Tooltip
                  title="The file must be in CSV format, separated by semicolons (;)"
                  arrow
                  placement="left"
                >
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item md={12}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item lg={2}>
                    <Button
                      variant="contained"
                      sx={{ width: "100%" }}
                      className="apeLigthBlueBackground"
                      onClick={handleUpload}
                    >
                      {intl.formatMessage({ id: "General.Upload" })}
                    </Button>
                  </Grid>
                  <Grid item lg={2}>
                    <Button
                      variant="contained"
                      sx={{ width: "100%" }}
                      className="apeLigthBlueBackground"
                      onClick={handleDownload}
                    >
                      {intl.formatMessage({ id: "General.Download" })}
                    </Button>
                  </Grid>
                  <Grid item lg={2}>
                    <Button
                      variant="contained"
                      sx={{ width: "100%" }}
                      className="apeLigthBlueBackground"
                      onClick={handleSearch}
                    >
                      {intl.formatMessage({ id: "General.Search" })}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        }
      />
      <CustomTablePagingInfo
        totalCount={totalCount}
        page={page.current}
        maxPage={maxPage.current}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
      <CustomTable
        columns={columns}
        rows={rows}
        id={"idUploadUDMListInfo"}
        rowHeight={70}
        headerHeight={80}
        tableHeight={tableHeigth}
      />
    </Box>
  );
};

export default UploadUDMList;
